import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Droplet, Waves, Eraser, Sparkles, Send, Loader2, PenLine, Paintbrush,
  GripVertical, ZoomIn, ZoomOut, RotateCcw, Hand, Box, Layers, Blend,
} from "lucide-react";

const ResinBoard3D = lazy(() => import("./ResinBoard3D"));

// Deep, high-concentration Jacquard Pearl Ex tones — 19 pigments total.
const COLORS = [
  { hex: "#8b6914", name: "Rich Deep Gold" },
  { hex: "#e6b800", name: "Solar Gold" },
  { hex: "#7a3b10", name: "Dark Burnt Copper" },
  { hex: "#6b4226", name: "Antique Bronze" },
  { hex: "#1a237e", name: "Deep Navy Sapphire" },
  { hex: "#2563eb", name: "True Blue" },
  { hex: "#5b9bd5", name: "Light Blue" },
  { hex: "#40e0d0", name: "Turquoise" },
  { hex: "#006666", name: "Deep Teal" },
  { hex: "#1b5e20", name: "Dark Forest Emerald" },
  { hex: "#6abf69", name: "Light Green" },
  { hex: "#4527a0", name: "Deep Royal Purple" },
  { hex: "#3d3d4d", name: "Gunmetal Silver" },
  { hex: "#a8a9ad", name: "Sterling Silver" },
  { hex: "#c8b88a", name: "Warm Ivory" },
  { hex: "#f0ece4", name: "Pearl White" },
  { hex: "#991b1b", name: "Crimson Red" },
  { hex: "#f06292", name: "Flamingo Pink" },
  { hex: "#1a1a1b", name: "Carbon Black" },
];

const CANVAS_W = 900;
const CANVAS_H = 600;

// Three board layouts David offers as blanks — the resin channel path is
// completely different for each but the pour/tool logic is unified through
// buildChannel() below.
const SHAPES = [
  { id: "straight", label: "Straight River", hint: "Classic two-slab river table" },
  { id: "winding",  label: "Winding River",  hint: "Organic meander through the timber" },
  { id: "cross",    label: "Cross Board",    hint: "Four quadrants split by a resin cross" },
];
const DEFAULT_SHAPE = "straight";

// --- deterministic RNG so knots/streaks don't shimmer between redraws ---
const seeded = (seed) => {
  let s = seed;
  return () => { s = (s * 1664525 + 1013904223) % 4294967296; return s / 4294967296; };
};

// --- HSL colour tweaking for the Tone (lightness) + Depth (saturation) sliders ---
function hexToHsl(hex) {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return { h, s, l };
}
function hslToHex(h, s, l) {
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r, g, b;
  if (s === 0) r = g = b = l;
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (v) => Math.round(v * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
// tone 0-100 (50 = neutral, ± shifts lightness by ±0.3), depth 0-100 (100 = full saturation)
function adjustColor(hex, tone, depth) {
  const { h, s, l } = hexToHsl(hex);
  const newL = Math.max(0, Math.min(1, l + ((tone - 50) / 100) * 0.6));
  const newS = Math.max(0, Math.min(1, s * (depth / 100)));
  return hslToHex(h, newS, newL);
}

// ---------- CHANNEL SHAPES ----------
// A single Path2D + hit-test predicate captures the resin area for every
// shape. Everything downstream (clip, hit-test, edges) reads from here.
function buildChannel(shape) {
  const path = new Path2D();
  if (shape === "cross") {
    const RW = 200, RH = 160;
    const rx = (CANVAS_W - RW) / 2, ry = (CANVAS_H - RH) / 2;
    path.rect(rx, 0, RW, CANVAS_H);
    path.rect(0, ry, CANVAS_W, RH);
    return {
      shape, path,
      hit: (x, y) => (x >= rx && x <= rx + RW) || (y >= ry && y <= ry + RH),
      // used by straight-pour to know if a tap is inside either channel
      centreX: CANVAS_W / 2, centreY: CANVAS_H / 2, rx, ry, RW, RH,
    };
  }
  if (shape === "winding") {
    const halfW = 95;
    const steps = 48;
    // river centre line — three gentle S-curves top-to-bottom
    const cx = (t) => CANVAS_W / 2 + Math.sin(t * Math.PI * 3) * 120;
    const centreLine = [];
    for (let i = 0; i <= steps; i++) centreLine.push({ t: i / steps, x: cx(i / steps), y: (i / steps) * CANVAS_H });
    // build polygon: left edge top→bottom, then right edge bottom→top
    path.moveTo(centreLine[0].x - halfW, 0);
    for (let i = 1; i <= steps; i++) path.lineTo(centreLine[i].x - halfW, centreLine[i].y);
    for (let i = steps; i >= 0; i--) path.lineTo(centreLine[i].x + halfW, centreLine[i].y);
    path.closePath();
    // hit test via approximate: distance from nearest centre-line sample
    const hit = (x, y) => {
      // clamp t to sample range
      const idx = Math.max(0, Math.min(steps, Math.round((y / CANVAS_H) * steps)));
      const p = centreLine[idx];
      return Math.abs(x - p.x) <= halfW;
    };
    return { shape, path, hit, centreLine, halfW };
  }
  // straight — horizontal river channel (wood on top and bottom, resin runs left→right)
  const RIVER_Y1 = 220, RIVER_Y2 = 380;
  path.rect(0, RIVER_Y1, CANVAS_W, RIVER_Y2 - RIVER_Y1);
  return {
    shape, path,
    hit: (x, y) => y >= RIVER_Y1 && y <= RIVER_Y2,
    RIVER_Y1, RIVER_Y2,
  };
}

// ---------- WOOD RENDERING ----------
// Draws a full-canvas Rimu wood slab with grain, knots, and varnish sheen.
// The channel bed is painted OVER the top of this, so a single wood pass
// works for all three shapes.
function drawWood(ctx, seed) {
  const w = CANVAS_W, h = CANVAS_H;
  const base = ctx.createLinearGradient(0, 0, 0, h);
  base.addColorStop(0, "#c68a4f");
  base.addColorStop(0.5, "#a86a34");
  base.addColorStop(1, "#84501f");
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, w, h);

  const rand = seeded(seed);
  const grainShades = ["#6b3a1a", "#7a4a2a", "#8a5426", "#a4693a", "#c4915a", "#d4a06a"];
  ctx.save();
  for (let i = 0; i < 300; i++) {
    const y = (i / 300) * h + Math.sin(i * 0.7 + seed) * 3;
    ctx.globalAlpha = 0.12 + rand() * 0.28;
    ctx.strokeStyle = grainShades[i % grainShades.length];
    ctx.lineWidth = 0.5 + rand() * 1.4;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(
      w * 0.28, y + (rand() - 0.5) * 10,
      w * 0.72, y + (rand() - 0.5) * 10,
      w,       y + (rand() - 0.5) * 6,
    );
    ctx.stroke();
  }
  ctx.restore();

  // Character streaks
  ctx.save();
  for (let i = 0; i < 20; i++) {
    const y = 30 + rand() * (h - 60);
    ctx.globalAlpha = 0.35 + rand() * 0.25;
    ctx.strokeStyle = rand() > 0.5 ? "#5c3512" : "#d9a26a";
    ctx.lineWidth = 1.2 + rand() * 1.8;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(w * 0.35, y + (rand() - 0.5) * 14, w * 0.65, y + (rand() - 0.5) * 14, w, y + (rand() - 0.5) * 8);
    ctx.stroke();
  }
  ctx.restore();

  // Knots
  ctx.save();
  const knots = 5 + Math.floor(rand() * 3);
  for (let k = 0; k < knots; k++) {
    const kx = 30 + rand() * (w - 60);
    const ky = 50 + rand() * (h - 100);
    const kr = 7 + rand() * 9;
    const kg = ctx.createRadialGradient(kx, ky, 0.5, kx, ky, kr);
    kg.addColorStop(0, "rgba(30,15,4,0.95)");
    kg.addColorStop(0.5, "rgba(60,32,12,0.72)");
    kg.addColorStop(1, "rgba(120,72,30,0)");
    ctx.fillStyle = kg;
    ctx.beginPath();
    ctx.ellipse(kx, ky, kr * 1.1, kr * 0.85, rand() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#4a2a0f";
    for (let r = 1; r < 5; r++) {
      ctx.globalAlpha = 0.22 - r * 0.03;
      ctx.lineWidth = 0.9;
      ctx.beginPath();
      ctx.ellipse(kx, ky, kr + r * 5, (kr + r * 5) * 0.75, 0, 0.15, Math.PI * 1.85);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }
  ctx.restore();

  // Diagonal varnish sheen
  const sheen = ctx.createLinearGradient(0, 0, w, h);
  sheen.addColorStop(0, "rgba(255,240,210,0)");
  sheen.addColorStop(0.4, "rgba(255,240,210,0.18)");
  sheen.addColorStop(0.5, "rgba(255,240,210,0.28)");
  sheen.addColorStop(0.6, "rgba(255,240,210,0.18)");
  sheen.addColorStop(1, "rgba(255,240,210,0)");
  ctx.fillStyle = sheen;
  ctx.fillRect(0, 0, w, h);

  // Top-face highlight
  const topGloss = ctx.createLinearGradient(0, 0, 0, 22);
  topGloss.addColorStop(0, "rgba(255,220,170,0.4)");
  topGloss.addColorStop(1, "rgba(255,220,170,0)");
  ctx.fillStyle = topGloss;
  ctx.fillRect(0, 0, w, 22);
}

// Draws the recessed channel bed + inset shadow + chamfer bevel using the
// current channel Path2D. Shape-agnostic thanks to shadowBlur + stroke tricks.
function drawChannelBed(ctx, channel) {
  ctx.save();
  // Dark bed within the path
  ctx.save();
  ctx.clip(channel.path);
  const rg = ctx.createLinearGradient(0, 0, 0, CANVAS_H);
  rg.addColorStop(0, "#140a04");
  rg.addColorStop(0.5, "#0a0602");
  rg.addColorStop(1, "#140a04");
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  // Inner shadow — stroke the path with a fat, blurred, black stroke; the
  // clip keeps only the inward blur, giving a real inset-shadow look.
  ctx.shadowColor = "rgba(0,0,0,0.95)";
  ctx.shadowBlur = 30;
  ctx.strokeStyle = "rgba(0,0,0,1)";
  ctx.lineWidth = 4;
  ctx.stroke(channel.path);
  ctx.restore();
  // Warm chamfer highlight on the outside of the cut
  ctx.lineWidth = 2;
  ctx.strokeStyle = "rgba(255,225,180,0.5)";
  ctx.stroke(channel.path);
  // Crisp inner lip
  ctx.lineWidth = 1.2;
  ctx.strokeStyle = "rgba(5,2,1,0.95)";
  ctx.stroke(channel.path);
  ctx.restore();
}

// =====================================================================
export default function ResinLab() {
  const canvasRef = useRef(null);       // visible canvas
  const overlayRef = useRef(null);      // offscreen: resin only
  const woodBaseRef = useRef(null);     // offscreen: wood only (no channel bed)
  const hitCtxRef = useRef(null);       // offscreen: hit-test canvas for isPointInPath
  const channelRef = useRef(buildChannel(DEFAULT_SHAPE));
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const lastMoveTime = useRef(0);
  const activePointers = useRef(new Map()); // for pinch-zoom
  const pinchStart = useRef(null);
  const designStartedTracked = useRef(false);

  const [boardShape, setBoardShape] = useState(DEFAULT_SHAPE);
  const [tool, setTool] = useState("pour");
  const [colorHex, setColorHex] = useState(COLORS[3].hex);
  const [tone, setTone] = useState(50);
  const [depth, setDepth] = useState(100);
  const [colorsUsed, setColorsUsed] = useState(new Set()); // stores ORIGINAL base hexes
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [show3D, setShow3D] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmShape, setConfirmShape] = useState(null); // pending shape switch
  const [showSend, setShowSend] = useState(false);
  const [form, setForm] = useState({ contact_name: "", contact_email: "", contact_phone: "", note: "" });
  const [sending, setSending] = useState(false);
  const [labPaused, setLabPaused] = useState(false);

  const MAX_COLORS = 3;
  const paletteLocked = colorsUsed.size >= MAX_COLORS;
  const isColorAvailable = (hex) => !paletteLocked || colorsUsed.has(hex);

  // The colour actually painted (after slider modulation).
  const effectiveColor = useMemo(() => adjustColor(colorHex, tone, depth), [colorHex, tone, depth]);
  const inChannel = (x, y) => channelRef.current.hit(x, y);

  // ---------- rendering ----------
  const compositeToVisibleCanvas = () => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    // Draw pre-baked wood, then channel bed, then resin overlay on top.
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.drawImage(woodBaseRef.current, 0, 0);
    drawChannelBed(ctx, channelRef.current);
    ctx.drawImage(overlayRef.current, 0, 0);
  };

  const rebuildWoodBase = () => {
    const wb = woodBaseRef.current;
    const wctx = wb.getContext("2d");
    wctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    // seed varies with shape to keep grain feeling different per blank
    drawWood(wctx, boardShape === "cross" ? 71 : boardShape === "winding" ? 53 : 29);
  };

  // ---------- init offscreen canvases ----------
  useEffect(() => {
    const mk = () => {
      const c = document.createElement("canvas");
      c.width = CANVAS_W; c.height = CANVAS_H;
      return c;
    };
    overlayRef.current = mk();
    woodBaseRef.current = mk();
    hitCtxRef.current = mk().getContext("2d"); // for isPointInPath needs
    rebuildWoodBase();
    compositeToVisibleCanvas();
    api.get("/settings/resin-lab-status").then((r) => setLabPaused(!!r.data?.paused)).catch(() => {});
    // Fire-and-forget page-view tracker for David's admin dashboard.
    api.post("/resin-lab/track", { type: "view" }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- shape change ----------
  useEffect(() => {
    channelRef.current = buildChannel(boardShape);
    rebuildWoodBase();
    if (overlayRef.current) {
      overlayRef.current.getContext("2d").clearRect(0, 0, CANVAS_W, CANVAS_H);
    }
    setColorsUsed(new Set());
    setDirty(false);
    designStartedTracked.current = false;
    compositeToVisibleCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardShape]);

  // ---------- coord translation (CSS transform doesn't matter — getBoundingClientRect returns transformed rect) ----------
  const getCoords = (e) => {
    const cvs = canvasRef.current;
    const rect = cvs.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((e.clientY - rect.top) / rect.height) * CANVAS_H,
    };
  };

  const tryUseColor = () => {
    if (colorsUsed.has(colorHex)) return true;
    if (colorsUsed.size >= MAX_COLORS) return false;
    setColorsUsed((s) => new Set(s).add(colorHex));
    return true;
  };

  const clipToChannel = (ctx) => ctx.clip(channelRef.current.path);

  // Fires the design_started tracker once per session — the moment the user
  // makes any actual mark. Guarded by a ref so we don't hammer the endpoint
  // on every subsequent stroke.
  const markDirty = () => {
    setDirty(true);
    if (!designStartedTracked.current) {
      designStartedTracked.current = true;
      api.post("/resin-lab/track", { type: "design_started" }).catch(() => {});
    }
  };

  const rgba = (hex, a) => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  };

  const paintDroplet = (x, y, radius, opacity = 1) => {
    const ctx = overlayRef.current.getContext("2d");
    ctx.save();
    clipToChannel(ctx);
    const c = effectiveColor;
    // Slightly translucent so pigment reads as WET resin rather than paint.
    const alpha = opacity * 0.82;
    const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
    g.addColorStop(0, rgba(c, alpha));
    g.addColorStop(0.55, rgba(c, alpha * 0.85));
    g.addColorStop(1, rgba(c, 0));
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2); ctx.fill();
    // Wet gloss specular — stronger, tighter highlight for a glossier look.
    const gx = x - radius * 0.3, gy = y - radius * 0.3, gr = radius * 0.36;
    const gloss = ctx.createRadialGradient(gx, gy, 0, gx, gy, gr);
    gloss.addColorStop(0, "rgba(255,255,255,0.22)");
    gloss.addColorStop(0.4, "rgba(255,255,255,0.09)");
    gloss.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = gloss;
    ctx.beginPath(); ctx.arc(gx, gy, gr, 0, Math.PI * 2); ctx.fill();
    // Sub-surface glimmer — a tiny bright pinpoint offset the other way so
    // the drop reads as a rounded liquid dome rather than a flat disc.
    const px = x + radius * 0.18, py = y + radius * 0.22;
    ctx.fillStyle = "rgba(255,255,255,0.08)";
    ctx.beginPath(); ctx.arc(px, py, radius * 0.12, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    markDirty();
  };

  const paintFlow = (x1, y1, x2, y2, speedPxPerMs = 0.5) => {
    const dx = x2 - x1, dy = y2 - y1;
    const dist = Math.hypot(dx, dy);
    const stops = Math.max(1, Math.ceil(dist / 5));
    const clamped = Math.max(0.3, Math.min(2.5, speedPxPerMs));
    const baseR = 44 - ((clamped - 0.3) / 2.2) * 26;
    const len = Math.max(1, dist);
    const px = -dy / len, py = dx / len;
    for (let i = 1; i <= stops; i++) {
      const t = i / stops;
      const jitter = (Math.random() - 0.5) * 12;
      const cx = x1 + dx * t + px * jitter;
      const cy = y1 + dy * t + py * jitter;
      if (!inChannel(cx, cy)) continue;
      const r = baseR + Math.sin(i * 0.9) * 3 + Math.random() * 3;
      paintDroplet(cx, cy, r, 0.92);
    }
  };

  const paintBrush = (x1, y1, x2, y2) => {
    if (!inChannel(x1, y1) && !inChannel(x2, y2)) return;
    const ctx = overlayRef.current.getContext("2d");
    ctx.save();
    clipToChannel(ctx);
    // Slightly translucent so brushstrokes read as wet resin, not opaque paint.
    ctx.globalAlpha = 0.82;
    ctx.strokeStyle = effectiveColor;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.lineWidth = 26;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    ctx.restore();
    markDirty();
  };

  // Straight Pour — shape-aware.
  //   • straight: full-width horizontal band centred on tap Y
  //   • cross:    full-height band on tap X (clipped to the cross)
  //   • winding:  drops droplets along the winding centre line so it fills the meander
  const straightPour = (x, y) => {
    if (!inChannel(x, y)) return;
    const ctx = overlayRef.current.getContext("2d");
    ctx.save();
    clipToChannel(ctx);
    const c = effectiveColor;
    const halfW = 17;
    if (channelRef.current.shape === "winding") {
      ctx.restore(); // release clip; paintDroplet will re-clip
      const line = channelRef.current.centreLine;
      for (const p of line) paintDroplet(p.x, p.y, 34, 0.9);
      markDirty();
      return;
    }
    if (channelRef.current.shape === "straight") {
      // Horizontal band centred on Y — spans the whole canvas width.
      const g = ctx.createLinearGradient(0, y - halfW, 0, y + halfW);
      g.addColorStop(0, rgba(c, 0));
      g.addColorStop(0.15, rgba(c, 0.85));
      g.addColorStop(0.5, rgba(c, 0.95));
      g.addColorStop(0.85, rgba(c, 0.85));
      g.addColorStop(1, rgba(c, 0));
      ctx.fillStyle = g;
      ctx.fillRect(0, y - halfW, CANVAS_W, halfW * 2);
      // gloss streak — horizontal
      const glossY = y - halfW * 0.35;
      const gl = ctx.createLinearGradient(0, glossY - 3, 0, glossY + 3);
      gl.addColorStop(0, "rgba(255,255,255,0)");
      gl.addColorStop(0.5, "rgba(255,255,255,0.15)");
      gl.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = gl;
      ctx.fillRect(0, glossY - 3, CANVAS_W, 6);
      ctx.restore();
      markDirty();
      return;
    }
    // cross — vertical band centred on X (clipped to the cross)
    const g = ctx.createLinearGradient(x - halfW, 0, x + halfW, 0);
    g.addColorStop(0, rgba(c, 0));
    g.addColorStop(0.15, rgba(c, 0.85));
    g.addColorStop(0.5, rgba(c, 0.95));
    g.addColorStop(0.85, rgba(c, 0.85));
    g.addColorStop(1, rgba(c, 0));
    ctx.fillStyle = g;
    ctx.fillRect(x - halfW, 0, halfW * 2, CANVAS_H);
    // gloss streak
    const glossX = x - halfW * 0.35;
    const gl = ctx.createLinearGradient(glossX - 3, 0, glossX + 3, 0);
    gl.addColorStop(0, "rgba(255,255,255,0)");
    gl.addColorStop(0.5, "rgba(255,255,255,0.15)");
    gl.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = gl;
    ctx.fillRect(glossX - 3, 0, 6, CANVAS_H);
    ctx.restore();
    markDirty();
  };

  const paintToothpick = (x1, y1, x2, y2) => {
    if (!inChannel(x2, y2)) return;
    const ctx = overlayRef.current.getContext("2d");
    let strokeColor;
    try {
      const px = ctx.getImageData(Math.round(x1), Math.round(y1), 1, 1).data;
      if (px[3] < 30) return;
      strokeColor = `rgba(${px[0]},${px[1]},${px[2]},${Math.min(1, px[3] / 255 + 0.15)})`;
    } catch (e) { return; }
    ctx.save();
    clipToChannel(ctx);
    ctx.strokeStyle = strokeColor;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    ctx.restore();
    compositeToVisibleCanvas();
    markDirty();
  };

  const swirl = (x1, y1, x2, y2) => {
    if (!inChannel(x2, y2)) return;
    const ctx = overlayRef.current.getContext("2d");
    ctx.save();
    clipToChannel(ctx);
    const size = 40;
    try {
      const patch = ctx.getImageData(Math.max(0, x1 - size / 2), Math.max(0, y1 - size / 2), size, size);
      const tmp = document.createElement("canvas");
      tmp.width = size; tmp.height = size;
      tmp.getContext("2d").putImageData(patch, 0, 0);
      ctx.globalAlpha = 0.75;
      ctx.drawImage(tmp, x2 - size / 2, y2 - size / 2);
      ctx.globalAlpha = 1.0;
    } catch (e) { /* ignore */ }
    ctx.restore();
    compositeToVisibleCanvas();
    markDirty();
  };

  // Blend — soft, blurry smudge. Samples a wide patch around the current
  // point, applies a canvas blur filter while drawing it back at low alpha
  // so adjacent colours *merge* into smooth gradients instead of getting
  // dragged (which is Swirl's job). The result is like David running a heat
  // gun over the resin — colour boundaries dissolve.
  const blendSmudge = (x, y) => {
    if (!inChannel(x, y)) return;
    const ctx = overlayRef.current.getContext("2d");
    const size = 70;
    try {
      const patch = ctx.getImageData(
        Math.max(0, x - size / 2),
        Math.max(0, y - size / 2),
        size, size,
      );
      // Ignore blends over bare bed — no resin under the tool means nothing to blend.
      let coverage = 0;
      for (let i = 3; i < patch.data.length; i += 4) if (patch.data[i] > 30) { coverage++; if (coverage > 20) break; }
      if (coverage < 20) return;
      const tmp = document.createElement("canvas");
      tmp.width = size; tmp.height = size;
      tmp.getContext("2d").putImageData(patch, 0, 0);
      ctx.save();
      clipToChannel(ctx);
      ctx.filter = "blur(6px)";
      ctx.globalAlpha = 0.45;
      ctx.drawImage(tmp, x - size / 2, y - size / 2);
      ctx.filter = "none";
      ctx.globalAlpha = 1.0;
      ctx.restore();
    } catch (e) { /* ignore */ }
    compositeToVisibleCanvas();
    markDirty();
  };

  // ---------- pointer events (with pinch-zoom + pan gestures) ----------
  const onPointerDown = (e) => {
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.current.size === 2) {
      const pts = [...activePointers.current.values()];
      pinchStart.current = {
        dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
        zoom, pan: { ...pan },
        cx: (pts[0].x + pts[1].x) / 2, cy: (pts[0].y + pts[1].y) / 2,
      };
      drawing.current = false;
      return;
    }
    e.preventDefault();
    const { x, y } = getCoords(e);
    if (tool === "hand") {
      drawing.current = true;
      lastPoint.current = { x: e.clientX, y: e.clientY };
      return;
    }
    if (!inChannel(x, y)) return;
    drawing.current = true;
    lastPoint.current = { x, y };
    lastMoveTime.current = performance.now();
    if (tool === "pour") { if (!tryUseColor()) return; paintDroplet(x, y, 48, 1); compositeToVisibleCanvas(); }
    else if (tool === "straight-pour") { if (!tryUseColor()) return; straightPour(x, y); compositeToVisibleCanvas(); }
    else if (tool === "paintbrush") { if (!tryUseColor()) return; paintBrush(x, y, x, y); compositeToVisibleCanvas(); }
  };
  const onPointerMove = (e) => {
    if (activePointers.current.has(e.pointerId)) activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // Pinch-zoom takes precedence when two fingers are down.
    if (activePointers.current.size === 2 && pinchStart.current) {
      const pts = [...activePointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const scale = Math.max(1, Math.min(4, pinchStart.current.zoom * (dist / pinchStart.current.dist)));
      setZoom(scale);
      return;
    }
    if (!drawing.current) return;
    e.preventDefault();
    if (tool === "hand") {
      const dx = e.clientX - lastPoint.current.x;
      const dy = e.clientY - lastPoint.current.y;
      setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
      lastPoint.current = { x: e.clientX, y: e.clientY };
      return;
    }
    const { x, y } = getCoords(e);
    const prev = lastPoint.current;
    const now = performance.now();
    const dt = Math.max(1, now - lastMoveTime.current);
    if (tool === "pour" && prev) {
      if (!inChannel(x, y) || !inChannel(prev.x, prev.y)) {
        lastPoint.current = { x, y }; lastMoveTime.current = now; return;
      }
      if (!tryUseColor()) return;
      paintFlow(prev.x, prev.y, x, y, Math.hypot(x - prev.x, y - prev.y) / dt);
      compositeToVisibleCanvas();
    } else if (tool === "swirl" && prev) { swirl(prev.x, prev.y, x, y); }
    else if (tool === "blend") { blendSmudge(x, y); }
    else if (tool === "paintbrush" && prev) {
      if (!tryUseColor()) return;
      paintBrush(prev.x, prev.y, x, y); compositeToVisibleCanvas();
    } else if (tool === "toothpick" && prev) { paintToothpick(prev.x, prev.y, x, y); }
    lastPoint.current = { x, y }; lastMoveTime.current = now;
  };
  const onPointerUp = (e) => {
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size < 2) pinchStart.current = null;
    drawing.current = false; lastPoint.current = null;
  };

  // Wheel = desktop zoom
  const onWheel = (e) => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1;
    setZoom((z) => Math.max(1, Math.min(4, z + dir * 0.15)));
  };

  const clearCanvas = () => {
    overlayRef.current.getContext("2d").clearRect(0, 0, CANVAS_W, CANVAS_H);
    setColorsUsed(new Set());
    setDirty(false);
    designStartedTracked.current = false;
    compositeToVisibleCanvas();
  };
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // ---------- submit ----------
  const submit = async () => {
    if (sending) return;
    if (!form.contact_name.trim()) return toast.error("Please enter your name.");
    if (!form.contact_email || !form.contact_email.includes("@")) return toast.error("Enter a valid email so David can reply to your design.");
    if (!form.contact_phone.trim() || form.contact_phone.replace(/\D/g, "").length < 6) return toast.error("Please enter a phone number David can reach you on.");
    setSending(true);
    try {
      const cvs = canvasRef.current;
      const dataUrl = cvs.toDataURL("image/png");
      const usedByName = [...colorsUsed].map((hex) => COLORS.find((c) => c.hex === hex)?.name || hex);
      const shapeLabel = SHAPES.find((s) => s.id === boardShape)?.label || boardShape;
      await api.post("/resin-lab/submit", {
        image_data_url: dataUrl,
        colors_used: usedByName,
        background: "rimu",
        contact_email: form.contact_email,
        contact_name: form.contact_name,
        contact_phone: form.contact_phone,
        note: `Board shape: ${shapeLabel}${form.note ? "\n\n" + form.note : ""}`,
      });
      toast.success("Design sent! David will be in touch soon.");
      setShowSend(false);
      setForm({ contact_name: "", contact_email: "", contact_phone: "", note: "" });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSending(false); }
  };

  const requestShapeChange = (id) => {
    if (id === boardShape) return;
    if (dirty) { setConfirmShape(id); return; }
    setBoardShape(id);
  };
  const acceptShapeChange = () => {
    if (confirmShape) setBoardShape(confirmShape);
    setConfirmShape(null);
  };

  // ---------- shape mini-icons ----------
  const ShapeIcon = ({ id }) => {
    if (id === "straight") {
      return (
        <svg viewBox="0 0 40 24" className="w-10 h-6"><rect x="0" y="0" width="40" height="9" fill="#a86a34"/><rect x="0" y="15" width="40" height="9" fill="#a86a34"/><rect x="0" y="9" width="40" height="6" fill="#0d0705"/></svg>
      );
    }
    if (id === "winding") {
      return (
        <svg viewBox="0 0 40 24" className="w-10 h-6"><rect x="0" y="2" width="40" height="20" fill="#a86a34"/><path d="M15 2 Q22 8 15 14 Q8 20 15 22 L25 22 Q18 20 25 14 Q32 8 25 2 Z" fill="#0d0705"/></svg>
      );
    }
    return (
      <svg viewBox="0 0 40 24" className="w-10 h-6"><rect x="0" y="2" width="40" height="20" fill="#a86a34"/><rect x="16" y="2" width="8" height="20" fill="#0d0705"/><rect x="0" y="10" width="40" height="4" fill="#0d0705"/></svg>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5DC] pb-24" data-testid="resin-lab-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-16">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles className="text-[#B87333]" size={28} />
          <p className="smallcaps !text-[#B87333]">Interactive</p>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-6xl !text-[#F5F5DC] leading-tight mb-3">
          The Resin Lab
        </h1>
        <p className="text-[#A3A3A3] max-w-2xl text-sm sm:text-base leading-relaxed mb-6">
          Pick a board blank, pour the resin, then send the sketch to the workshop. I&apos;ll build it in solid recycled Rimu.
        </p>

        {/* SHAPE SELECTOR */}
        <div className="mb-6" data-testid="shape-selector">
          <div className="flex items-center justify-between mb-3">
            <p className="smallcaps">Board Blank</p>
            {dirty && (
              <button type="button" onClick={() => setConfirmShape(boardShape === "straight" ? null : "straight")} className="text-[10px] uppercase tracking-widest text-[#B87333] hover:text-[#F5F5DC]" data-testid="change-shape-link">
                Change shape
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {SHAPES.map((s) => {
              const active = boardShape === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => requestShapeChange(s.id)}
                  data-testid={`shape-${s.id}`}
                  aria-pressed={active}
                  className={`min-h-[76px] p-2 sm:p-3 border-2 flex flex-col items-center justify-center gap-1.5 text-center transition-all ${
                    active ? "border-[#B87333] bg-[#B87333]/10" : "border-[#2A2A2A] hover:border-[#B87333]/50"
                  }`}
                >
                  <ShapeIcon id={s.id} />
                  <span className="text-[11px] sm:text-xs uppercase tracking-widest text-[#F5F5DC]">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid lg:grid-cols-[240px_1fr] gap-6">
          {/* Toolbar */}
          <div className="space-y-6">
            <div>
              <p className="smallcaps mb-3 !text-[#B87333]">1. The Pour</p>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                {[
                  { id: "pour",          Icon: Droplet,      label: "Pour" },
                  { id: "straight-pour", Icon: GripVertical, label: "Straight Pour" },
                ].map(({ id, Icon, label }) => (
                  <button key={id} onClick={() => setTool(id)} data-testid={`tool-${id}`}
                    className={`h-11 px-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest border-2 ${
                      tool === id ? "border-[#B87333] text-[#B87333]" : "border-[#2A2A2A] text-[#F5F5DC] hover:border-[#B87333]/60"
                    }`}>
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>
              <p className="smallcaps mt-5 mb-3 !text-[#B87333]">2. The Detail</p>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                {[
                  { id: "swirl",      Icon: Waves,      label: "Swirl" },
                  { id: "paintbrush", Icon: Paintbrush, label: "Paintbrush" },
                  { id: "toothpick",  Icon: PenLine,    label: "Toothpick" },
                  { id: "blend",      Icon: Blend,      label: "Blend" },
                ].map(({ id, Icon, label }) => (
                  <button key={id} onClick={() => setTool(id)} data-testid={`tool-${id}`}
                    className={`h-11 px-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest border-2 ${
                      tool === id ? "border-[#B87333] text-[#B87333]" : "border-[#2A2A2A] text-[#F5F5DC] hover:border-[#B87333]/60"
                    }`}>
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>
              <p className="smallcaps mt-5 mb-3">Canvas</p>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
                <button onClick={() => setTool("hand")} data-testid="tool-hand"
                  className={`h-11 px-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest border-2 ${
                    tool === "hand" ? "border-[#B87333] text-[#B87333]" : "border-[#2A2A2A] text-[#F5F5DC] hover:border-[#B87333]/60"
                  }`}>
                  <Hand size={14} /> Hand
                </button>
                <button onClick={clearCanvas} data-testid="tool-eraser"
                  className="h-11 px-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest border-2 border-[#2A2A2A] text-[#F5F5DC] hover:border-[#D32F2F] hover:text-[#D32F2F]">
                  <Eraser size={14} /> Eraser
                </button>
              </div>
            </div>

            {/* Tone + Depth sliders */}
            <div>
              <p className="smallcaps mb-3">Colour Mix</p>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">
                    <span>Tone</span><span>{tone < 50 ? "Darker" : tone > 50 ? "Brighter" : "Neutral"}</span>
                  </div>
                  <input type="range" min="0" max="100" value={tone} onChange={(e) => setTone(+e.target.value)} data-testid="slider-tone" className="w-full accent-[#B87333]" />
                </div>
                <div>
                  <div className="flex justify-between text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">
                    <span>Depth</span><span>{depth}%</span>
                  </div>
                  <input type="range" min="10" max="100" value={depth} onChange={(e) => setDepth(+e.target.value)} data-testid="slider-depth" className="w-full accent-[#B87333]" />
                </div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                  <span>Preview</span>
                  <span className="w-6 h-6 border border-[#2A2A2A]" style={{ background: effectiveColor }} data-testid="colour-preview" />
                  <span className="font-mono normal-case tracking-normal">{effectiveColor}</span>
                </div>
              </div>
            </div>

            <div>
              <p className="smallcaps mb-3">Pearl Ex Palette</p>
              <div className="grid grid-cols-4 lg:grid-cols-2 gap-2">
                {COLORS.map((c) => {
                  const selected = c.hex === colorHex;
                  const available = isColorAvailable(c.hex);
                  return (
                    <button key={c.hex} onClick={() => { if (available) setColorHex(c.hex); }} disabled={!available}
                      data-testid={`color-${c.hex.replace('#','')}`} aria-label={c.name} aria-disabled={!available}
                      className={`h-14 rounded-md border-2 transition-all ${
                        selected ? "border-[#B87333] scale-105" : "border-[#2A2A2A] hover:border-[#B87333]/50"
                      } ${available ? "opacity-100 cursor-pointer" : "opacity-25 grayscale cursor-not-allowed"}`}
                      style={{ background: c.hex }} />
                  );
                })}
              </div>
              <p className="text-[10px] text-[#757575] mt-2 uppercase tracking-widest">
                {COLORS.find((c) => c.hex === colorHex)?.name}
              </p>
              {paletteLocked && (
                <p data-testid="palette-limit-msg" className="mt-3 text-[11px] text-[#B87333] font-bold uppercase tracking-widest leading-snug">
                  ★ 3 color limit reached — clear canvas to start over
                </p>
              )}
            </div>

            {labPaused ? (
              <div className="w-full min-h-[56px] border-2 border-[#B87333] bg-[#B87333]/10 p-4 text-center" data-testid="lab-paused-message">
                <p className="text-[#B87333] text-xs sm:text-sm font-bold leading-relaxed">
                  The Lab is currently at full capacity! We aren&apos;t taking new simulator orders right now, but check back soon.
                </p>
              </div>
            ) : (
              <button onClick={() => setShowSend(true)} data-testid="build-this-for-me-btn"
                className="w-full h-14 min-h-[56px] inline-flex items-center justify-center gap-2 bg-[#B87333] text-white font-bold uppercase tracking-wider text-xs hover:bg-[#A6632B] transition-colors">
                <Send size={16} /> Build This For Me
              </button>
            )}
          </div>

          {/* Canvas + zoom + 3D toggle */}
          <div className="w-full">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="flex items-center gap-1">
                <button onClick={() => setZoom((z) => Math.max(1, z - 0.25))} data-testid="zoom-out"
                  className="p-2 border border-[#2A2A2A] hover:border-[#B87333]"><ZoomOut size={14} /></button>
                <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} data-testid="zoom-in"
                  className="p-2 border border-[#2A2A2A] hover:border-[#B87333]"><ZoomIn size={14} /></button>
                <button onClick={resetView} data-testid="reset-view"
                  className="p-2 border border-[#2A2A2A] hover:border-[#B87333]"><RotateCcw size={14} /></button>
                <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3] ml-2 font-mono">{zoom.toFixed(2)}x</span>
              </div>
              <button onClick={() => setShow3D((v) => !v)} data-testid="toggle-3d"
                className={`h-9 px-3 inline-flex items-center gap-2 text-xs uppercase tracking-widest border-2 ${
                  show3D ? "border-[#B87333] text-[#B87333]" : "border-[#2A2A2A] text-[#F5F5DC] hover:border-[#B87333]/60"
                }`}>
                {show3D ? <Layers size={14} /> : <Box size={14} />} {show3D ? "2D View" : "3D Preview"}
              </button>
            </div>
            <div className="bg-[#111] border border-[#2A2A2A] p-2 sm:p-4 overflow-hidden" style={{ aspectRatio: `${CANVAS_W}/${CANVAS_H}` }}>
              {!show3D ? (
                <div className="w-full h-full overflow-hidden">
                  <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} data-testid="resin-canvas"
                    className={`w-full h-full touch-none block ${tool === "hand" ? "cursor-grab" : "cursor-crosshair"}`}
                    style={{ transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`, transformOrigin: "center center", touchAction: "none", transition: pinchStart.current || drawing.current ? "none" : "transform 0.12s" }}
                    onPointerDown={onPointerDown} onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp} onPointerLeave={onPointerUp} onPointerCancel={onPointerUp}
                    onWheel={onWheel} />
                </div>
              ) : (
                <Suspense fallback={<div className="w-full h-full flex items-center justify-center text-[#A3A3A3] text-xs">Loading 3D preview…</div>}>
                  <ResinBoard3D compositeCanvas={canvasRef.current} />
                </Suspense>
              )}
            </div>
            <p className="text-[10px] text-[#757575] mt-2 uppercase tracking-widest">
              {show3D ? "Drag to orbit · pinch or scroll to zoom" : "Wheel to zoom · pinch on mobile · use Hand tool to pan"}
            </p>
          </div>
        </div>
      </div>

      {/* Shape-switch confirmation */}
      {confirmShape && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="shape-confirm-modal">
          <div className="bg-[#0A0A0A] border border-[#2A2A2A] w-full max-w-sm p-6">
            <h3 className="font-display text-xl !text-[#F5F5DC] mb-2">Change board blank?</h3>
            <p className="text-sm text-[#A3A3A3] mb-5">This will clear your current design so we can lay out the new shape.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmShape(null)} className="btn-ghost px-4">Keep working</button>
              <button onClick={acceptShapeChange} className="btn-primary" data-testid="shape-confirm-yes">Change shape</button>
            </div>
          </div>
        </div>
      )}

      {/* Send modal */}
      {showSend && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-4" data-testid="build-modal">
          <div className="bg-[#0A0A0A] border border-[#2A2A2A] w-full max-w-md p-6">
            <h2 className="font-display text-2xl !text-[#F5F5DC] mb-1">Build This For Me</h2>
            <p className="text-xs text-[#A3A3A3] mb-5">
              I&apos;ll receive your design + colours as a new quote and reply within a day or two.
            </p>
            <div className="space-y-3">
              <input type="text" data-testid="build-name" className="input-dark min-h-[48px]" placeholder="Full name *" autoComplete="name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              <input type="email" data-testid="build-email" className="input-dark min-h-[48px]" placeholder="Email address *" autoComplete="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
              <input type="tel" data-testid="build-phone" className="input-dark min-h-[48px]" placeholder="Phone number *" autoComplete="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              <textarea rows={3} data-testid="build-note" className="input-dark" placeholder="Optional note — size, deadline, anything else" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={() => setShowSend(false)} disabled={sending} className="btn-ghost px-4">Cancel</button>
              <button onClick={submit} disabled={sending} data-testid="build-submit" className="btn-primary min-h-[52px]">
                {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send Design</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
